import React, { useEffect, useState } from "react";
import { Button, DefaultComponentProps, Dialog, DialogActions, DialogContent, DialogTitle, FlexGrid, Typography } from '@albo-ui/core';
import { Box, Radio, RadioGroup, FormControlLabel, TextField, Paper } from "@mui/material";
import { ManualCheckPayload, MatchDetails } from "./pldStatus";

export type ModalManualCheckProps = {
    open: boolean;
    onClose: () => void;
    onSaveResult?: (result: ManualCheckPayload) => void;
    matchDetails?: MatchDetails;
} & DefaultComponentProps;

type Decision = "approved" | "rejected";

type DecisionOption = {
    value: Decision;
    title: string;
    help: string;
    accent: string;
    accentBg: string;
    radioColor: "success" | "error";
};

const DECISION_OPTIONS: DecisionOption[] = [
    {
        value: "approved",
        title: "Aprobado",
        help: "Se validó manualmente y la coincidencia permite que se pueda continuar avanzando en el onboarding.",
        accent: "#27AE60",
        accentBg: "#F4FBF7",
        radioColor: "success",
    },
    {
        value: "rejected",
        title: "Rechazado",
        help: "Se validó manualmente y la coincidencia impide que la cuenta pueda abrirse.",
        accent: "#C0392B",
        accentBg: "#FFF5F5",
        radioColor: "error",
    },
];

const getAgentEmail = (): string | undefined => {
    try {
        return localStorage.session ? JSON.parse(localStorage.session)?.email : undefined;
    } catch {
        return undefined;
    }
};

export const ModalManualCheck = ({ open, onClose, onSaveResult, matchDetails }: ModalManualCheckProps) => {
    const [decision, setDecision] = useState<any>("");
    const [comment, setComment] = useState("");
    const [isSaving, setIsSaving] = useState(false);
    const [zPos, setZPos] = useState(45);

    useEffect(() => {
        if (!open) return;
        const manualCheck = matchDetails?.manualcheck;
        setDecision(manualCheck?.status || "");
        setComment(manualCheck?.detail || "");
    }, [open, matchDetails]);

    const handleSave = async () => {
        if (!matchDetails) return;

        setIsSaving(true);
        setZPos(-1);
        await onSaveResult?.({
            uuid: matchDetails.uuid,
            manualcheck: {
                status: decision,
                detail: comment,
                resolved_by: getAgentEmail() || "",
                resolved_at: new Date().toISOString(),
            },
        });
        setIsSaving(false);
        setDecision("approved");
        setComment("");
        setZPos(45);
        onClose();
    };

    const metadataRows = [
        { label: "Entidad", value: matchDetails?.entity, strong: true },
        { label: "Nombre completo", value: matchDetails?.fullName, strong: true },
        { label: "Identificador", value: matchDetails?.identifier, strong: false },
    ];

    return (
        <Dialog onClose={onClose} open={open} style={{ display: zPos !== 45 ? "none" : null, width: 700, maxWidth: "100vw", maxHeight: "90vh" }}>
            <DialogTitle>
                <Box sx={{ mb: 1 }}>
                    <Typography variant="h2" style={{ fontSize: "1.2rem", fontWeight: 700 }}>
                        Cruce manual — Manual Check
                    </Typography>
                    <Typography variant="paragraph" color="lightNeutral" style={{ fontSize: "0.85rem", marginTop: 4, display: "block" }}>
                        El cruce en listas restringidas marcó una coincidencia inexacta: solo se cuenta con el nombre completo y no fue posible validar la fecha de nacimiento. Manual Check revisa por aparte y registra el resultado.
                    </Typography>
                </Box>
            </DialogTitle>

            {matchDetails && (
                <DialogContent>
                    <FlexGrid fullWidth direction="column" alignItems="flex-start" spacing={3}>
                        {/* Metadata del match */}
                        <Paper elevation={0} sx={{ p: 2, backgroundColor: "#F8F9FA", border: "1px solid #E9ECEF", borderRadius: 2, width: "100%" }}>
                            <Box sx={{ display: "grid", gridTemplateColumns: "150px 1fr", rowGap: 1.2, fontSize: "0.85rem" }}>
                                {metadataRows.map((row) => (
                                    <React.Fragment key={row.label}>
                                        <Typography variant="paragraph" color="lightNeutral" style={{ fontWeight: 500 }}>
                                            {row.label}
                                        </Typography>
                                        <Typography variant="paragraph" style={row.strong ? { fontWeight: 700 } : { color: "#495057" }}>
                                            {row.value}
                                        </Typography>
                                    </React.Fragment>
                                ))}
                            </Box>
                        </Paper>

                        {/* Resultado del cruce manual */}
                        <Box sx={{ width: "100%", mt: 4 }}>
                            <Typography variant="smallTitle" style={{ fontWeight: 700, fontSize: "0.95rem", marginBottom: 12, display: "block" }}>
                                Resultado del cruce manual
                            </Typography>

                            <RadioGroup value={decision} onChange={(e) => setDecision(e.target.value as Decision)}>
                                {DECISION_OPTIONS.map((option, index) => {
                                    const selected = decision === option.value;
                                    return (
                                        <Paper
                                            key={option.value}
                                            elevation={0}
                                            sx={{
                                                p: 2,
                                                mb: index === 0 ? 1.5 : 0,
                                                border: selected ? `2px solid ${option.accent}` : "1px solid #CED4DA",
                                                borderRadius: 2,
                                                backgroundColor: selected ? option.accentBg : "#FFFFFF",
                                                cursor: "pointer",
                                            }}
                                            onClick={() => setDecision(option.value)}
                                        >
                                            <FormControlLabel
                                                value={option.value}
                                                control={<Radio color={option.radioColor} sx={{ p: 0, pr: "9px" }} />}
                                                label={
                                                    <Box>
                                                        <Typography variant="smallTitle" style={{ fontWeight: 700, color: option.accent, fontSize: "0.95rem" }}>
                                                            {option.title}
                                                        </Typography>
                                                        <Typography variant="paragraph" color="lightNeutral" style={{ fontSize: "0.82rem", marginTop: 2, display: "block" }}>
                                                            {option.help}
                                                        </Typography>
                                                    </Box>
                                                }
                                                sx={{ alignItems: "flex-start", margin: 0, width: "100%" }}
                                            />
                                        </Paper>
                                    );
                                })}
                            </RadioGroup>
                        </Box>

                        {/* Evidencia / Comentario */}
                        <Box sx={{ width: "100%", mt: 4 }}>
                            <Typography variant="paragraph" style={{ fontWeight: 600, fontSize: "0.85rem", marginBottom: 6, display: "block", color: "#495057" }}>
                                Evidencia del cruce manual
                            </Typography>
                            <Typography variant="paragraph" color="lightNeutral" style={{ fontSize: "0.82rem", display: "block" }}>
                                Al aprobar, indica si se trata de un falso positivo o si es un PEP que queremos como cliente.
                            </Typography>
                            <TextField
                                multiline
                                rows={3}
                                fullWidth
                                placeholder="Si se aprueba, indica si se trata de un falso positivo o si es un PEP que queremos como cliente. Listas y fuentes consultadas, identificadores comparados, conclusión…"
                                value={comment}
                                onChange={(e) => setComment(e.target.value)}
                                sx={{ backgroundColor: "#FFFFFF" }}
                            />
                        </Box>
                    </FlexGrid>
                </DialogContent>
            )}

            <DialogActions sx={{ p: 2, pt: 1 }}>
                <FlexGrid container fullWidth justify="space-between" alignItems="center">
                    <Button buttonType="secondary" onClick={onClose} size="normal" disabled={isSaving}>
                        Cancelar
                    </Button>
                    <Button onClick={handleSave} size="normal" disabled={isSaving || comment.trim() === ""}>
                        {isSaving ? "Guardando..." : "Guardar resultado"}
                    </Button>
                </FlexGrid>
            </DialogActions>
        </Dialog>
    );
};
